import { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";
import type { Unit } from "@/lib/types/unit";

export interface ComparisonModal {
  id: string;
  unit: Unit;
  position: { x: number; y: number };
}

interface ComparisonModalContextType {
  modals: ComparisonModal[];
  openComparisonModal: (unit: Unit) => void;
  closeComparisonModal: (id: string) => void;
  closeAllComparisonModals: () => void;
  getOpenModals: () => ComparisonModal[];
  updateModalPosition: (id: string, position: { x: number; y: number }) => void;
  maxModals: number;
}

const ComparisonModalContext = createContext<ComparisonModalContextType | undefined>(undefined);

const DEFAULT_MAX_MODALS = 4;
const MAX_MODALS_LIMIT = 8;

interface ComparisonModalProviderProps {
  children: ReactNode;
  maxModals?: number;
}

// Calculate position for a modal based on its index
function calculateModalPosition(index: number, total: number): { x: number; y: number } {
  // Ensure we're in browser environment
  if (typeof window === "undefined") {
    return { x: 100, y: 100 };
  }
  
  const modalWidth = 448; // max-w-md = 448px
  const gap = 16;
  const marginFromRight = 16;
  const marginFromTop = 100; // Top offset
  
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  
  // Position modals on right side, stacked vertically
  const x = Math.max(0, windowWidth - modalWidth - marginFromRight);
  
  // Estimate modal height for vertical spacing
  const estimatedModalHeight = 600;
  const spacing = estimatedModalHeight + gap;
  
  // Calculate Y position, ensuring modals don't go off bottom of screen
  let y = marginFromTop + index * spacing;
  
  // If modals would go off bottom, adjust to fit
  const maxY = windowHeight - estimatedModalHeight - marginFromRight;
  if (y > maxY) {
    // Stack from bottom instead
    y = Math.max(marginFromTop, maxY - (total - index - 1) * spacing);
  }
  
  return {
    x: Math.max(0, x),
    y: Math.max(marginFromTop, y),
  };
}

export function ComparisonModalProvider({ 
  children, 
  maxModals = DEFAULT_MAX_MODALS 
}: ComparisonModalProviderProps) {
  const [modals, setModals] = useState<ComparisonModal[]>([]);

  const openComparisonModal = useCallback((unit: Unit) => {
    setModals((currentModals) => {
      // Check if unit is already open
      const existingModal = currentModals.find((m) => m.unit.descriptorName === unit.descriptorName);
      if (existingModal) {
        // Already open - no need to change anything, DOM order handles layering
        return currentModals;
      }

      // Calculate position using current window dimensions
      const newIndex = currentModals.length;
      const newTotal = newIndex + 1;
      const position = calculateModalPosition(newIndex, newTotal);

      // Check limit
      if (currentModals.length >= Math.min(maxModals, MAX_MODALS_LIMIT)) {
        // Remove oldest modal (first in array)
        const newModals = currentModals.slice(1);
        
        return [
          ...newModals,
          {
            id: `${unit.descriptorName}-${Date.now()}`,
            unit,
            position,
          },
        ];
      }

      // Add new modal
      return [
        ...currentModals,
        {
          id: `${unit.descriptorName}-${Date.now()}`,
          unit,
          position,
        },
      ];
    });
  }, [maxModals]);

  const closeComparisonModal = useCallback((id: string) => {
    setModals((currentModals) => {
      return currentModals.filter((m) => m.id !== id);
      // Don't recalculate positions - let users keep their manual positioning
    });
  }, []);

  const updateModalPosition = useCallback((id: string, newPosition: { x: number; y: number }) => {
    setModals((currentModals) =>
      currentModals.map((modal) =>
        modal.id === id ? { ...modal, position: newPosition } : modal
      )
    );
  }, []);

  const closeAllComparisonModals = useCallback(() => {
    setModals([]);
  }, []);

  const getOpenModals = useCallback(() => {
    return modals;
  }, [modals]);

  return (
    <ComparisonModalContext.Provider
      value={{
        modals,
        openComparisonModal,
        closeComparisonModal,
        closeAllComparisonModals,
        getOpenModals,
        updateModalPosition,
        maxModals: Math.min(maxModals, MAX_MODALS_LIMIT),
      }}
    >
      {children}
    </ComparisonModalContext.Provider>
  );
}

export function useComparisonModals() {
  const context = useContext(ComparisonModalContext);
  if (context === undefined) {
    throw new Error("useComparisonModals must be used within a ComparisonModalProvider");
  }
  return context;
}

